import React, { useState, lazy, memo } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from './Button';
import { FadeIn } from './FadeIn';
import { trackEvent } from '../utils/metaPixel';
/**
 * Offer section — premium grid value stack (silent-luxury aesthetic).
 *  - Main product card on top (shows ~$37~ regular price)
 *  - 3 bonus guides as an editorial grid with on-image price overlays
 *  - Final pricing card is a clean itemized table:
 *      Main product ($37 → 83% off)
 *      + 3 bonuses ($97 / $97 / $67 → all GRATIS)
 *      = Valor total ~$298~ → Tu inversión hoy $6.36
 *  - CTA redirects to the Shopify checkout for SKU 53815682957678.
 */
type MainItem = {
  label: string;
  desc: string;
  value: string;
};
type BonusItem = {
  image: string;
  imageAlt: string;
  label: string;
  desc: string;
  fullDesc: string[];
  value: string;
};
const MAIN_ITEM: MainItem = {
  label: 'Cambio Hormonal: El Método del 1%',
  desc: 'El protocolo completo en formato e-book digital — acceso inmediato para teléfono, tablet o computadora.',
  value: '$37'
};
const BONUS_ITEMS: BonusItem[] = [{
  image: 'https://cdn.magicpatterns.com/uploads/aQpwkaEzFnFf2Fo2y4jPRq/Bono_1.png',
  imageAlt: 'Manos sosteniendo una taza de té caliente',
  label: 'Bono #1 · Protocolo SOS Antojos',
  desc: 'Apaga la ansiedad por dulce y los antojos nocturnos sin usar fuerza de voluntad.',
  fullDesc: ['El protocolo SOS Antojos es tu guía rápida para los momentos en los que la ansiedad por dulce, harinas o picoteo nocturno te ataca con más fuerza.', 'Adentro encontrarás:', '• La fórmula de los 3 minutos para apagar un antojo sin recurrir a la fuerza de voluntad.', '• 7 swaps inteligentes que satisfacen el antojo sin desbalancear tu glucosa.', '• El "kit de emergencia" que debes tener siempre en tu cocina para esos días en que todo arde.', 'Diseñado para que retomes el control sin culpa y sin restricción extrema.'],
  value: '$97'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/jBqdYnBJLd8y6hu12k7zAq/Bono_2.png',
  imageAlt: 'Plato con aguacate, huevo poché, espinaca y salmón sobre mesa de mármol',
  label: 'Bono #2 · Recetario Hormonal de 15 Min',
  desc: 'Recetas hormonalmente inteligentes para días sin tiempo, listas en 15 minutos.',
  fullDesc: ['Más de 30 recetas pensadas para mujeres con SOP, hipotiroidismo o desequilibrios hormonales que también tienen vida fuera de la cocina.', 'Cada receta:', '• Lista en 15 minutos o menos, con ingredientes que ya tienes en casa.', '• Equilibrada en proteína, fibra y grasas saludables para sostener la energía todo el día.', '• Etiquetada por momento del día y por fase del ciclo para que sepas qué te conviene cuándo.', 'Desayunos, almuerzos, snacks y cenas — sin sentir que estás "a dieta".'],
  value: '$97'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/daXSfygqmHVfqyKdqVvzeR/Bono_3.png',
  imageAlt: 'Escritorio minimalista con iPad y planta verde',
  label: 'Bono #3 · Checklist de Supervivencia',
  desc: 'Tu mapa diario imprimible para mantener el método en piloto automático.',
  fullDesc: ['El checklist imprimible (y digital) que convierte el Método del 1% en un piloto automático que no depende de tu memoria ni de tu motivación.', 'Adentro encontrarás:', '• Tu rutina mañana / tarde / noche en una sola página, lista para imprimir o usar en el teléfono.', '• Un tracker semanal de síntomas para que veas tu progreso real (más allá de cómo te sientes ese día).', '• Las 5 decisiones clave que debes tomar antes de las 10 am para que el resto del día fluya.', 'Ideal para esas semanas en que apenas tienes tiempo de respirar — el método te sigue acompañando.'],
  value: '$67'
}];
// Pricing math (kept here so it's easy to tweak in one place)
const FINAL_PRICE = 6.36;
const MAIN_REGULAR_PRICE = 37;
const TOTAL_REGULAR = 298; // 37 + 97 + 97 + 67
const MAIN_DISCOUNT_PCT = Math.round((MAIN_REGULAR_PRICE - FINAL_PRICE) / MAIN_REGULAR_PRICE * 100); // 83
/**
 * Individual bonus card with its own expand/collapse state.
 * The short `desc` shows by default; a bordo "Leer más" link reveals the
 * full multi-paragraph description with a smooth animation.
 */
function BonusCard({
  bonus
}: {
  bonus: BonusItem;
}) {
  const [expanded, setExpanded] = useState(false);
  return <div className="bg-cream border border-ink/5 shadow-soft hover:shadow-soft-lg transition-shadow duration-500 flex flex-col overflow-hidden" data-id="element-16">
      <div className="relative aspect-[4/3] bg-sage/30 overflow-hidden" data-id="element-17">
        <img src={bonus.image} alt={bonus.imageAlt} width={480} height={360} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" data-id="element-18" />

        {/* Strikethrough price — top-right */}
        <div className="absolute top-3 right-3 bg-cream/95 backdrop-blur-sm px-2.5 py-1 shadow-soft" data-id="element-19">
          <span className="font-serif text-sm md:text-base text-ink-light tabular-nums" translate="no" style={{
          textDecoration: 'line-through',
          textDecorationColor: '#B89665',
          textDecorationThickness: '2px'
        }} data-id="element-20">
            {bonus.value}
          </span>
        </div>
      </div>

      <div className="p-6 md:p-7 flex flex-col flex-1" data-id="element-21">
        <h4 className="font-serif text-lg md:text-xl text-ink mb-2 leading-tight" data-id="element-22">
          {bonus.label}
        </h4>
        <p className="text-sm text-ink-light font-light leading-relaxed" data-id="element-23">
          {bonus.desc}
        </p>

        {/* CSS-only expand/collapse using the grid-template-rows trick.
            Far cheaper than framer-motion's JS-measured height animation —
            no measure-then-animate, no per-mount AnimatePresence overhead,
            and works smoothly even on low-end mobile devices. */}
        <div className="grid transition-[grid-template-rows,opacity] duration-300 ease-out" style={{
        gridTemplateRows: expanded ? '1fr' : '0fr',
        opacity: expanded ? 1 : 0
      }} aria-hidden={!expanded} data-id="element-24">
          <div className="overflow-hidden" data-id="element-25">
            <div className="pt-3 mt-3 border-t border-ink/5 space-y-2.5" data-id="element-26">
              {bonus.fullDesc.map((paragraph, i) => <p key={i} className="text-sm text-ink-light font-light leading-relaxed whitespace-pre-line" data-id="element-27">
                  {paragraph}
                </p>)}
            </div>
          </div>
        </div>

        <button type="button" onClick={() => setExpanded(v => !v)} aria-expanded={expanded} className="mt-4 self-start inline-flex items-center gap-1.5 text-[11px] md:text-xs uppercase tracking-[0.18em] font-medium text-bordo hover:text-bordo-dark border-b border-bordo/40 hover:border-bordo pb-0.5 transition-colors duration-300" data-id="element-28">
          <span data-id="element-29">{expanded ? 'Leer menos' : 'Leer más'}</span>
          <ChevronDown strokeWidth={1.5} className={`w-3 h-3 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} data-id="element-30" />
        </button>
      </div>
    </div>;
}
export function Bundle() {
  const handleCheckout = () => {
    trackEvent('AddToCart', {
      content_name: 'El Método del 1% - Bundle Completo',
      content_ids: ['53815682957678'],
      content_type: 'product',
      value: FINAL_PRICE,
      currency: 'USD'
    });
    window.location.assign('https://vuelveavivir.com/cart/53815682957678:1');
  };
  // Build a unified line-item table for the final pricing card
  const LINE_ITEMS: Array<{
    label: string;
    price: string;
    badge: string;
    badgeStyle: 'discount' | 'gratis';
  }> = [{
    label: MAIN_ITEM.label,
    price: MAIN_ITEM.value,
    badge: `-${MAIN_DISCOUNT_PCT}%`,
    badgeStyle: 'discount'
  }, ...BONUS_ITEMS.map(b => ({
    label: b.label,
    price: b.value,
    badge: 'GRATIS',
    badgeStyle: 'gratis' as const
  }))];
  return <section id="oferta" className="py-16 md:py-24 bg-sage scroll-mt-24" data-id="element-31">
      <div className="max-w-6xl mx-auto px-6 md:px-12" data-id="element-32">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16" data-id="element-33">
          <FadeIn data-id="element-34">
            <span className="uppercase tracking-[0.2em] text-xs text-ink-light mb-4 block" data-id="element-35">
              La Guía Maestra
            </span>
            <h2 className="font-serif text-3xl md:text-5xl text-ink mb-5 md:mb-6" data-id="element-36">
              Todo lo que necesitas para <em className="italic" data-id="element-37">sanar</em>.
            </h2>
            <p className="text-base md:text-lg text-ink-light font-light leading-relaxed max-w-2xl mx-auto" data-id="element-38">
              El método principal más 3 bonos exclusivos cuidadosamente
              diseñados para que tu progreso no dependa de la fuerza de voluntad
              ningún día.
            </p>
          </FadeIn>
        </div>

        {/* Bonus grid */}
        <FadeIn delay={0.1} data-id="element-39">
          <div className="mb-10 md:mb-12" data-id="element-40">
            <div className="text-center mb-6 md:mb-8" data-id="element-41">
              <span className="uppercase tracking-[0.2em] text-xs text-bordo font-medium" data-id="element-42">
                + 3 Bonos exclusivos · Gratis hoy
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 items-start" data-id="element-43">
              {BONUS_ITEMS.map(bonus => <BonusCard key={bonus.label} bonus={bonus} data-id="element-44" />)}
            </div>
          </div>
        </FadeIn>

        {/* Final pricing table + CTA */}
        <FadeIn delay={0.2} data-id="element-45">
          <div id="precio" className="bg-cream border border-ink/5 shadow-soft-lg scroll-mt-32 max-w-2xl mx-auto" data-id="element-46">
            {/* Itemized table */}
            <div className="px-6 md:px-10 pt-7 md:pt-8 pb-2" data-id="element-47">
              <p className="uppercase tracking-[0.2em] text-[10px] md:text-xs text-ink-light text-center mb-5 md:mb-6" data-id="element-48">
                Tu paquete completo
              </p>

              <ul className="divide-y divide-ink/10" data-id="element-49">
                {LINE_ITEMS.map((item, i) => <li key={i} className="py-3 md:py-3.5 flex items-center gap-3 md:gap-4" data-id="element-50">
                    <span className="flex-1 text-sm md:text-base text-ink font-light leading-snug min-w-0" data-id="element-51">
                      {item.label}
                    </span>
                    <span className="font-serif text-base md:text-lg text-ink-light tabular-nums flex-shrink-0" translate="no" style={{
                  textDecoration: 'line-through',
                  textDecorationColor: '#B89665',
                  textDecorationThickness: '2px'
                }} data-id="element-52">
                      {item.price}
                    </span>
                    <span className={`flex-shrink-0 text-[10px] md:text-[11px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 ${item.badgeStyle === 'gratis' ? 'text-sand-dark bg-sand/15' : 'text-bordo bg-bordo/10'}`} data-id="element-53">
                      {item.badge}
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* Total regular */}
            <div className="px-6 md:px-10 py-4 md:py-5 bg-sand/5 border-t-2 border-sand/30 flex items-center justify-between" data-id="element-54">
              <span className="uppercase tracking-[0.18em] text-[10px] md:text-xs text-ink-light" data-id="element-55">
                Valor total regular
              </span>
              <span className="font-serif text-xl md:text-2xl text-ink-light tabular-nums" translate="no" style={{
              textDecoration: 'line-through',
              textDecorationColor: '#B89665',
              textDecorationThickness: '2px'
            }} data-id="element-56">
                ${TOTAL_REGULAR}
              </span>
            </div>

            {/* Final price + CTA — redirects to Shopify */}
            <div className="px-6 md:px-10 py-8 md:py-10 bg-sand/15 flex flex-col items-center text-center" data-id="element-57">
              <p className="text-[10px] md:text-xs text-ink-light uppercase tracking-widest mb-6 md:mb-7" data-id="element-58">
                Pago único · Acceso de por vida
              </p>

              <Button onClick={handleCheckout} className="w-full sm:w-auto" data-id="element-59">
                Comprar ahora — ${FINAL_PRICE.toFixed(2)}
              </Button>

              <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[10px] uppercase tracking-[0.16em] text-ink-light" data-id="element-60">
                <li className="inline-flex items-center gap-1.5" data-id="element-61">
                  <Check strokeWidth={2} className="w-3 h-3 text-sand-dark" data-id="element-62" />
                  Acceso inmediato
                </li>
                <li className="inline-flex items-center gap-1.5" data-id="element-63">
                  <Check strokeWidth={2} className="w-3 h-3 text-sand-dark" data-id="element-64" />
                  Para siempre
                </li>
                <li className="inline-flex items-center gap-1.5" data-id="element-65">
                  <Check strokeWidth={2} className="w-3 h-3 text-sand-dark" data-id="element-66" />
                  Garantía 30 días
                </li>
              </ul>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}